/**
 * AnnualLeaveTab — Her personelin ücretli yıllık izin takibi.
 *
 * Hak ediş (İş Kanunu Md. 53): başlangıç tarihine göre tamamlanmış hizmet
 * yıllarından otomatik hesaplanır — 1-5 yıl: 14, 6-15 yıl: 20, 16+: 26 iş günü.
 * Girilen izinler kullanılan toplamdan düşülür; kalan < 0 ise satır kırmızı.
 */
import React, { useEffect, useMemo, useState } from "react";
import { LoadingRow } from "@/components/LoadingState";
import { toast } from "sonner";
import {
  Plus, Edit, Trash2, Search, X, CalendarDays, AlertTriangle, CheckCircle2, Clock, Calendar,
  Download, LayoutGrid, Table as TableIcon, ChevronLeft, ChevronRight, Hourglass,
} from "lucide-react";
import { api, API, fmtDate } from "@/lib/api";
import { getToken } from "@/lib/api";
import { getPozisyonColor } from "@/lib/pozisyonColors";
import BulkLeaveModal from "@/components/BulkLeaveModal";
import { DateInput } from "@/components/DateInput";
import { SortProvider, STh, useColumnSort } from "@/components/SortTh";
import { Paperclip } from "lucide-react";
import { LeaveDocs } from "@/components/leave/LeaveDocs";
import { LeaveHistory } from "@/components/leave/LeaveHistory";
import { DeletedLeaves } from "@/components/leave/DeletedLeaves";

export default function AnnualLeaveTab() {
  const [summary, setSummary] = useState([]);
  const [arefeHalfDay, setArefeHalfDay] = useState(false);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [expandedPid, setExpandedPid] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const [editingLeave, setEditingLeave] = useState(null);
  const [prefillPid, setPrefillPid] = useState(null);
  const [view, setView] = useState("table"); // "table" | "calendar"
  const today = new Date();
  const [calYear, setCalYear] = useState(today.getFullYear());
  const [calMonth, setCalMonth] = useState(today.getMonth() + 1);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/annual-leave/summary");
      setSummary(r.data.rows || []);
      setArefeHalfDay(!!r.data.arefe_half_day);
    } catch {
      toast.error("İzin özeti yüklenemedi");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return summary;
    return summary.filter((r) =>
      (r.ad_soyad || "").toLowerCase().includes(q) ||
      (r.sicil_no || "").toLowerCase().includes(q) ||
      (r.pozisyon || "").toLowerCase().includes(q)
    );
  }, [summary, search]);
  const leaveSort = useColumnSort(filtered, [], { hizmet: (r) => (r.hizmet_yili || 0) * 12 + (r.hizmet_ay || 0) });

  // Aggregate totals for KPI strip
  const totals = useMemo(() => {
    const t = { entitled: 0, used: 0, remaining: 0, overCount: 0, lowCount: 0, arefeDays: 0 };
    for (const r of filtered) {
      t.entitled += r.hak_edilen;
      t.used += r.kullanilan;
      t.remaining += r.kalan;
      t.arefeDays += (r.arefe_yarim_gun_sayisi || 0);
      if (r.kalan < 0) t.overCount += 1;
      else if (r.kalan > 0 && r.kalan <= 3) t.lowCount += 1;
    }
    return t;
  }, [filtered]);

  const rowClass = (r) => {
    if (r.kalan < 0) return "bg-rose-100 hover:bg-rose-200";
    if (r.hak_edilen === 0) return "bg-slate-50 hover:bg-slate-100";
    if (r.kalan === 0) return "bg-amber-50 hover:bg-amber-100";
    if (r.kalan <= 3) return "bg-yellow-50 hover:bg-yellow-100";
    return "hover:bg-emerald-50/40";
  };

  return (
    <div className="space-y-4" data-testid="annual-leave-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display font-black text-2xl text-slate-900 flex items-center gap-2">
            <CalendarDays size={22} className="text-teal-600" />
            Yıllık İzin Takibi
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            İş Kanunu Md. 53 — Hizmet yılı başına: 1-5 yıl <strong>14 gün</strong>, 6-15 yıl <strong>20 gün</strong>, 16+ yıl <strong>26 gün</strong>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-md border border-slate-300 overflow-hidden" data-testid="view-switcher">
            <button
              onClick={() => setView("table")}
              className={`px-2 py-1.5 text-xs font-semibold inline-flex items-center gap-1 ${view === "table" ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
              data-testid="view-table-btn"
            >
              <TableIcon size={12} /> Tablo
            </button>
            <button
              onClick={() => setView("calendar")}
              className={`px-2 py-1.5 text-xs font-semibold inline-flex items-center gap-1 ${view === "calendar" ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
              data-testid="view-calendar-btn"
            >
              <LayoutGrid size={12} /> Takvim
            </button>
          </div>
          <div className="relative">
            <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Personel ara..."
              className="pl-7 pr-2 py-1.5 border border-slate-300 rounded-md text-sm focus:border-slate-900 focus:ring-2 focus:ring-slate-900/10 outline-none"
              data-testid="annual-leave-search"
            />
          </div>
          <button
            onClick={() => setShowBulk(true)}
            data-testid="annual-leave-bulk-btn"
            className="inline-flex items-center gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold px-3 py-2 rounded-md"
          >
            <Plus size={14} /> Toplu İzin
          </button>
          <button
            onClick={() => { setPrefillPid(null); setEditingLeave(null); setShowAdd(true); }}
            data-testid="annual-leave-add-btn"
            className="inline-flex items-center gap-1.5 bg-teal-600 hover:bg-teal-700 text-white text-sm font-semibold px-3 py-2 rounded-md"
          >
            <Plus size={14} /> İzin Ekle
          </button>
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="annual-leave-kpis">
        <KpiCard icon={CheckCircle2} label="Personel" value={filtered.length} tone="slate" />
        <KpiCard icon={Clock} label="Toplam Hak Edilen" value={totals.entitled.toFixed(0) + " gün"} tone="indigo" />
        <KpiCard icon={CalendarDays} label="Kullanılan" value={totals.used.toFixed(1) + " gün"} tone="amber" />
        <KpiCard icon={Calendar} label="Toplam Kalan" value={totals.remaining.toFixed(1) + " gün"} tone="emerald" />
        <KpiCard icon={AlertTriangle} label="Hak Aşımı" value={totals.overCount + " kişi"} tone={totals.overCount ? "rose" : "slate"} />
      </div>

      {view === "calendar" ? (
        <LeaveCalendar
          year={calYear}
          month={calMonth}
          onYearChange={setCalYear}
          onMonthChange={setCalMonth}
          onEditLeave={(lv) => { setEditingLeave(lv); setShowAdd(true); }}
        />
      ) : (
      <div className="sticky-table border border-slate-200 rounded-lg bg-white">
        <SortProvider value={leaveSort}>
        <table className="w-full text-sm" data-testid="annual-leave-table">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <STh k="sicil_no" className="text-left px-3 py-2 font-bold text-slate-700">TC Kimlik No</STh>
              <STh k="ad_soyad" className="text-left px-3 py-2 font-bold text-slate-700">Ad Soyad</STh>
              <STh k="pozisyon" className="text-left px-3 py-2 font-bold text-slate-700">Pozisyon</STh>
              <STh k="ise_baslama_tarihi" className="text-left px-3 py-2 font-bold text-slate-700">İşe Başlama</STh>
              <STh k="hizmet" className="text-left px-3 py-2 font-bold text-slate-700">Hizmet</STh>
              <STh k="hak_edilen" className="text-right px-3 py-2 font-bold text-slate-700">Hak Edilen</STh>
              <STh k="kullanilan" className="text-right px-3 py-2 font-bold text-slate-700">Kullanılan</STh>
              <STh k="arefe_yarim_gun_sayisi" className="text-right px-3 py-2 font-bold text-slate-700" title="Yarım gün sayılan arefe günleri (iş günü modunda alınan izinlerdeki)">
                Arefe {arefeHalfDay ? "½" : <span className="text-slate-400 font-normal">(kapalı)</span>}
              </STh>
              <STh k="kalan" className="text-right px-3 py-2 font-bold text-slate-700">Kalan</STh>
              <th className="text-right px-3 py-2 font-bold text-slate-700"></th>
            </tr>
          </thead>
          <tbody>
            {loading && <LoadingRow colSpan={10} text="İzinler yükleniyor..." />}
            {!loading && filtered.length === 0 && <tr><td colSpan={10} className="text-center text-slate-400 py-6">Kayıt yok</td></tr>}
            {!loading && leaveSort.sorted.map((r) => (
              <React.Fragment key={r.personnel_id}>
                <tr
                  className={`border-b border-slate-100 cursor-pointer ${rowClass(r)}`}
                  onClick={() => setExpandedPid(expandedPid === r.personnel_id ? null : r.personnel_id)}
                  data-testid={`leave-row-${r.personnel_id}`}
                >
                  <td className="px-3 py-2 font-mono text-xs text-slate-600">{r.sicil_no || "-"}</td>
                  <td className="px-3 py-2 font-semibold text-slate-900">
                    <div className="flex items-center gap-2">
                      <span>{r.ad_soyad}</span>
                      {r.kalan < 0 && (
                        <span
                          className="inline-flex items-center gap-0.5 text-[9px] font-black uppercase bg-rose-100 text-rose-800 border border-rose-300 px-1.5 py-0.5 rounded animate-pulse"
                          title={`Yıllık izin hakkı aşıldı. Hak: ${r.hak_edilen} · Kullanılan: ${r.kullanilan} · Kalan: ${r.kalan}`}
                          data-testid={`hak-asimi-leave-${r.personnel_id}`}
                        >
                          ⚠ HAK AŞIMI
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-600">{r.pozisyon || "-"}</td>
                  <td className="px-3 py-2 text-xs text-slate-600">{fmtDate(r.ise_baslama_tarihi) || "-"}</td>
                  <td className="px-3 py-2 text-xs text-slate-600">
                    {r.hizmet_yili > 0 || r.hizmet_ay > 0
                      ? `${r.hizmet_yili} yıl ${r.hizmet_ay} ay`
                      : <span className="italic text-slate-400">yeni</span>}
                  </td>
                  <td className="px-3 py-2 text-right font-bold text-indigo-800" data-testid={`leave-entitled-${r.personnel_id}`}>{r.hak_edilen}</td>
                  <td className="px-3 py-2 text-right text-amber-800" data-testid={`leave-used-${r.personnel_id}`}>{r.kullanilan.toFixed(1).replace(/\.0$/, "")}</td>
                  <td className="px-3 py-2 text-right" data-testid={`leave-arefe-${r.personnel_id}`}>
                    {(r.arefe_yarim_gun_sayisi || 0) > 0 ? (
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-bold ${
                          arefeHalfDay ? "bg-indigo-50 border-indigo-200 text-indigo-800" : "bg-slate-100 border-slate-200 text-slate-600"
                        }`}
                        title={arefeHalfDay
                          ? `${r.arefe_yarim_gun_sayisi} arefe günü · toplam etki -${(r.arefe_yarim_gun_etkisi || 0).toFixed(1)} gün`
                          : `${r.arefe_yarim_gun_sayisi} arefe günü (yarım gün toggle KAPALI — tam gün sayıldı)`}
                      >
                        <Hourglass size={10}/>
                        {r.arefe_yarim_gun_sayisi}{arefeHalfDay ? "× ½" : "× 1"}
                      </span>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </td>
                  <td className={`px-3 py-2 text-right font-black ${r.kalan < 0 ? "text-rose-700" : r.kalan === 0 ? "text-amber-700" : "text-emerald-700"}`} data-testid={`leave-remaining-${r.personnel_id}`}>
                    {r.kalan.toFixed(1).replace(/\.0$/, "")}
                  </td>
                  <td className="px-3 py-2 text-right" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => { setPrefillPid(r.personnel_id); setEditingLeave(null); setShowAdd(true); }}
                      data-testid={`leave-add-for-${r.personnel_id}`}
                      className="inline-flex items-center gap-1 bg-teal-100 hover:bg-teal-200 text-teal-800 border border-teal-300 text-[11px] font-semibold px-2 py-1 rounded"
                    >
                      <Plus size={11}/> İzin
                    </button>
                  </td>
                </tr>
                {expandedPid === r.personnel_id && (
                  <tr className="bg-slate-50/60">
                    <td colSpan={10} className="px-6 py-3">
                      <LeaveDetails
                        personnelId={r.personnel_id}
                        onEdit={(lv) => { setEditingLeave(lv); setShowAdd(true); }}
                        onChanged={load}
                      />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
        </SortProvider>
      </div>
      )}

      {showAdd && (
        <LeaveModal
          editing={editingLeave}
          personnelId={prefillPid}
          personnel={summary}
          onClose={() => { setShowAdd(false); setEditingLeave(null); setPrefillPid(null); }}
          onSaved={() => { setShowAdd(false); setEditingLeave(null); setPrefillPid(null); load(); }}
        />
      )}
      {showBulk && (
        <BulkLeaveModal
          onClose={() => setShowBulk(false)}
          onSaved={() => { setShowBulk(false); load(); }}
        />
      )}
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, tone }) {
  const palette = {
    slate: "bg-slate-100 text-slate-700 border-slate-300",
    indigo: "bg-indigo-100 text-indigo-800 border-indigo-300",
    amber: "bg-amber-100 text-amber-800 border-amber-300",
    emerald: "bg-emerald-100 text-emerald-800 border-emerald-300",
    rose: "bg-rose-100 text-rose-800 border-rose-300",
  }[tone] || "bg-slate-100";
  return (
    <div className={`border rounded-lg p-3 flex items-center gap-2 ${palette}`}>
      <Icon size={18} />
      <div>
        <div className="text-[10px] font-bold uppercase tracking-wider opacity-80">{label}</div>
        <div className="text-lg font-black">{value}</div>
      </div>
    </div>
  );
}

function LeaveDetails({ personnelId, onEdit, onChanged }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [tick, setTick] = useState(0);

  const load = async () => {
    setLoading(true);
    setTick((t) => t + 1);
    try {
      const r = await api.get(`/annual-leave?personnel_id=${personnelId}`);
      setRows(r.data.rows || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [personnelId]);

  const handleDelete = async (lv) => {
    if (!window.confirm(`İzin kaydı silinecek (${fmtDate(lv.baslangic)} → ${fmtDate(lv.bitis)}). Onaylıyor musunuz?`)) return;
    try {
      await api.delete(`/annual-leave/${lv.id}`);
      toast.success("İzin silindi");
      load();
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Silinemedi"); }
  };

  if (loading) return <div className="text-xs italic text-slate-500 py-2">Yükleniyor...</div>;
  if (!rows.length) return (
    <div>
      <div className="text-xs italic text-slate-500 py-2">Bu personel için izin kaydı bulunmuyor.</div>
      <DeletedLeaves personnelId={personnelId} refreshKey={tick} />
    </div>
  );

  return (
    <div className="space-y-1" data-testid={`leave-details-${personnelId}`}>
      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">İzin Kayıtları ({rows.length})</div>
      <div className="space-y-1">
        {rows.map((lv) => (
          <React.Fragment key={lv.id}>
          <div className="bg-white border border-slate-200 rounded-md px-3 py-1.5 flex items-center gap-3 text-xs" data-testid={`leave-item-${lv.id}`}>
            <CalendarDays size={13} className="text-teal-600 shrink-0" />
            <div className="flex-1 flex items-center gap-4 flex-wrap">
              <span className="font-semibold">{fmtDate(lv.baslangic)} → {fmtDate(lv.bitis)}</span>
              <span className="inline-flex items-center gap-1 bg-amber-100 text-amber-800 border border-amber-200 px-1.5 py-0.5 rounded font-bold">
                {lv.gun_sayisi} gün
              </span>
              <TurBadge tur={lv.tur || "yillik"} />
              {lv.aciklama && <span className="italic text-slate-500">{lv.aciklama}</span>}
              {lv.is_gunu_modu && <span className="inline-flex items-center gap-1 text-[10px] bg-sky-100 text-sky-800 border border-sky-200 px-1.5 py-0.5 rounded font-bold">İŞ GÜNÜ</span>}
            </div>
            <a
              href={`${API}/annual-leave/${lv.id}/certificate`}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => {
                // Attach auth token for the browser-triggered download via fetch
                e.preventDefault();
                const token = getToken();
                fetch(`${API}/annual-leave/${lv.id}/certificate`, { headers: { Authorization: `Bearer ${token}` } })
                  .then((r) => r.blob())
                  .then((blob) => {
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = `izin_belgesi_${lv.baslangic}.pdf`;
                    document.body.appendChild(a); a.click(); a.remove();
                    URL.revokeObjectURL(url);
                  })
                  .catch(() => toast.error("Belge indirilemedi"));
              }}
              data-testid={`leave-pdf-${lv.id}`}
              className="w-6 h-6 grid place-items-center text-teal-700 hover:bg-teal-50 rounded"
              title="İzin belgesi PDF indir"
            >
              <Download size={11}/>
            </a>
            <button
              onClick={() => setOpenId(openId === lv.id ? null : lv.id)}
              data-testid={`leave-docs-toggle-${lv.id}`}
              className={`w-6 h-6 grid place-items-center rounded ${openId === lv.id ? "bg-teal-100 text-teal-800" : "text-teal-700 hover:bg-teal-50"}`}
              title="Belgeler ve kayıt geçmişi"
            >
              <Paperclip size={11}/>
            </button>
            <button
              onClick={() => onEdit(lv)}
              data-testid={`leave-edit-${lv.id}`}
              className="w-6 h-6 grid place-items-center text-indigo-600 hover:bg-indigo-50 rounded"
              title="Düzenle"
            >
              <Edit size={11}/>
            </button>
            <button
              onClick={() => handleDelete(lv)}
              data-testid={`leave-delete-${lv.id}`}
              className="w-6 h-6 grid place-items-center text-rose-600 hover:bg-rose-50 rounded"
              title="Sil"
            >
              <Trash2 size={11}/>
            </button>
          </div>
          {openId === lv.id && (
            <div className="bg-white border border-teal-200 rounded-md px-3 py-2 grid md:grid-cols-2 gap-4" data-testid={`leave-extras-${lv.id}`}>
              <LeaveDocs leaveId={lv.id} onChanged={() => setTick((t) => t + 1)} />
              <LeaveHistory leaveId={lv.id} refreshKey={tick} />
            </div>
          )}
          </React.Fragment>
        ))}
      </div>
      <DeletedLeaves personnelId={personnelId} refreshKey={tick} />
    </div>
  );
}

export function LeaveModal({ editing, personnelId, personnel, onClose, onSaved }) {
  const isEdit = !!editing?.id;
  const [form, setForm] = useState(() => ({
    personnel_id: editing?.personnel_id || personnelId || "",
    baslangic: editing?.baslangic || new Date().toISOString().slice(0, 10),
    bitis: editing?.bitis || new Date().toISOString().slice(0, 10),
    gun_sayisi: editing?.gun_sayisi ?? null,
    aciklama: editing?.aciklama || "",
    is_gunu_modu: editing?.is_gunu_modu ?? false,
    tur: editing?.tur || "yillik",
    _auto: editing?.gun_sayisi == null, // auto by default on new
  }));
  const [saving, setSaving] = useState(false);
  const [persSearch, setPersSearch] = useState("");

  // Auto-calc gun_sayisi when start/end/is_gunu_modu changes AND user hasn't overridden.
  // Business day mode: skip Sat/Sun + Turkish public holidays.
  const _FIXED_H = {
    "01-01": 1, "04-23": 1, "05-01": 1, "05-19": 1,
    "07-15": 1, "08-30": 1, "10-29": 1,
  };
  const _RELIGIOUS_H = {
    "2025-03-30":1,"2025-03-31":1,"2025-04-01":1,"2026-03-20":1,"2026-03-21":1,"2026-03-22":1,
    "2027-03-09":1,"2027-03-10":1,"2027-03-11":1,
    "2025-06-06":1,"2025-06-07":1,"2025-06-08":1,"2025-06-09":1,
    "2026-05-27":1,"2026-05-28":1,"2026-05-29":1,"2026-05-30":1,
    "2027-05-16":1,"2027-05-17":1,"2027-05-18":1,"2027-05-19":1,
  };
  const _isHoliday = (d) => {
    const mmdd = `${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
    const yyyymmdd = `${d.getFullYear()}-${mmdd}`;
    return !!(_FIXED_H[mmdd] || _RELIGIOUS_H[yyyymmdd]);
  };
  const computedDays = useMemo(() => {
    if (!form.baslangic || !form.bitis) return 0;
    const s = new Date(form.baslangic + "T00:00:00");
    const e = new Date(form.bitis + "T00:00:00");
    if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return 0;
    if (!form.is_gunu_modu) {
      const d = Math.floor((e - s) / 86400000) + 1;
      return d > 0 ? d : 0;
    }
    // Business day loop
    let cnt = 0;
    const cur = new Date(s.getTime());
    while (cur <= e) {
      const dow = cur.getDay();
      // 0=Sun ... 6=Sat; skip Sat (6) + Sun (0) + holidays
      if (dow !== 0 && dow !== 6 && !_isHoliday(cur)) cnt += 1;
      cur.setDate(cur.getDate() + 1);
    }
    return cnt;
  }, [form.baslangic, form.bitis, form.is_gunu_modu]);

  useEffect(() => {
    if (form._auto) setForm((f) => ({ ...f, gun_sayisi: computedDays }));
  }, [computedDays, form._auto]);

  const selected = personnel.find((p) => p.personnel_id === form.personnel_id);
  const persFiltered = personnel.filter((p) =>
    (p.ad_soyad || "").toLowerCase().includes(persSearch.toLowerCase()) ||
    (p.sicil_no || "").toLowerCase().includes(persSearch.toLowerCase())
  );

  const save = async () => {
    if (!form.personnel_id) { toast.error("Personel seçin"); return; }
    if (!form.baslangic || !form.bitis) { toast.error("Başlangıç ve bitiş tarihi gerekli"); return; }
    if (new Date(form.bitis) < new Date(form.baslangic)) { toast.error("Bitiş tarihi başlangıçtan önce olamaz"); return; }
    const gs = form.gun_sayisi ?? computedDays;
    if (gs <= 0) { toast.error("Gün sayısı 0'dan büyük olmalı"); return; }
    const payload = {
      personnel_id: form.personnel_id,
      baslangic: form.baslangic,
      bitis: form.bitis,
      gun_sayisi: form._auto ? null : Number(gs),
      aciklama: form.aciklama || "",
      is_gunu_modu: !!form.is_gunu_modu,
      tur: form.tur || "yillik",
    };
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/annual-leave/${editing.id}`, payload);
        toast.success("İzin güncellendi");
      } else {
        await api.post("/annual-leave", payload);
        toast.success("İzin kaydedildi");
      }
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kaydedilemedi");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-40 bg-black/40 grid place-items-center p-4" onClick={onClose} data-testid="annual-leave-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
          <h3 className="font-display font-extrabold text-slate-900 flex items-center gap-2">
            <CalendarDays size={16} className="text-teal-600" />
            {isEdit ? "İzin Düzenle" : "Yeni İzin Ekle"}
          </h3>
          <button onClick={onClose} className="w-7 h-7 grid place-items-center hover:bg-slate-100 rounded"><X size={14}/></button>
        </div>
        <div className="p-4 space-y-3">
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-700 block mb-1">Personel</label>
            {selected && (
              <div className="inline-flex items-center gap-2 bg-teal-50 border border-teal-300 rounded-md px-2 py-1 text-xs mb-2">
                <strong>{selected.ad_soyad}</strong>
                <span className="text-slate-500">· {selected.pozisyon}</span>
                <span className="ml-2 text-slate-600">
                  Hak: <strong className="text-indigo-800">{selected.hak_edilen}</strong>
                  {" / Kalan: "}
                  <strong className={selected.kalan < 0 ? "text-rose-700" : "text-emerald-700"}>{selected.kalan}</strong>
                </span>
              </div>
            )}
            <input
              value={persSearch}
              onChange={(e) => setPersSearch(e.target.value)}
              placeholder="Personel ara..."
              className="w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm mb-2 focus:border-slate-900 focus:ring-2 focus:ring-slate-900/10 outline-none"
            />
            <div className="border border-slate-200 rounded-md max-h-40 overflow-y-auto p-1 space-y-0.5" data-testid="leave-personnel-list">
              {persFiltered.slice(0, 50).map((p) => (
                <label
                  key={p.personnel_id}
                  className={`flex items-center gap-2 text-xs cursor-pointer px-1.5 py-1 rounded ${form.personnel_id === p.personnel_id ? "bg-teal-100" : "hover:bg-slate-50"}`}
                >
                  <input
                    type="radio"
                    name="pers"
                    checked={form.personnel_id === p.personnel_id}
                    onChange={() => setForm({ ...form, personnel_id: p.personnel_id })}
                    data-testid={`leave-pers-${p.sicil_no}`}
                  />
                  <span className="font-semibold flex-1">{p.ad_soyad}</span>
                  <span className="text-slate-500">{p.sicil_no}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-slate-700 block mb-1">Başlangıç</label>
              <DateInput
                value={form.baslangic}
                onChange={(e) => setForm({ ...form, baslangic: e.target.value, _auto: true })}
                className="w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm"
                data-testid="leave-baslangic"
              />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-slate-700 block mb-1">Bitiş</label>
              <DateInput
                value={form.bitis}
                min={form.baslangic || undefined}
                onChange={(e) => setForm({ ...form, bitis: e.target.value, _auto: true })}
                className="w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm"
                data-testid="leave-bitis"
              />
            </div>
          </div>

          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-700 block mb-1">İzin Türü</label>
            <select
              value={form.tur || "yillik"}
              onChange={(e) => setForm({ ...form, tur: e.target.value })}
              className="w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm"
              data-testid="leave-tur"
            >
              <option value="yillik">🏖️ Yıllık İzin (hak düşer, ücretli)</option>
              <option value="ucretli_izin">💰 Ücretli İzin (hak düşer, ücretli)</option>
              <option value="rapor">🏥 Rapor (hak düşmez, ücretli)</option>
              <option value="ucretsiz_izin">⚪ Ücretsiz İzin (hak düşmez, ücret kesilir)</option>
            </select>
            <div className="text-[10px] text-slate-500 mt-1">
              Yıllık ve ücretli izin, bordrodaki "Ücretli İzin" sütununa otomatik eklenir. Rapor → "Raporlu Gün", Ücretsiz → "Ücretsiz İzin".
            </div>
          </div>

          <label className="inline-flex items-center gap-2 text-xs text-slate-700 cursor-pointer bg-sky-50 border border-sky-200 rounded-md px-2.5 py-1.5 w-full">
            <input
              type="checkbox"
              checked={!!form.is_gunu_modu}
              onChange={(e) => setForm({ ...form, is_gunu_modu: e.target.checked })}
              data-testid="leave-is-gunu-modu"
            />
            <strong>İş Günü Modu</strong>
            <span className="text-slate-500">— hafta sonu ve resmi tatiller gün sayısından çıkarılır (Pzt-Cum, bayramlar hariç)</span>
          </label>

          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-700 block mb-1 flex items-center gap-2">
              Gün Sayısı
              <label className="inline-flex items-center gap-1 ml-auto text-[10px] font-normal normal-case">
                <input
                  type="checkbox"
                  checked={form._auto}
                  onChange={(e) => setForm({ ...form, _auto: e.target.checked })}
                  data-testid="leave-auto-days"
                /> Otomatik hesapla ({computedDays})
              </label>
            </label>
            <input
              type="number"
              min="0.5"
              step="0.5"
              value={form._auto ? computedDays : (form.gun_sayisi ?? "")}
              disabled={form._auto}
              onChange={(e) => setForm({ ...form, gun_sayisi: Number(e.target.value) })}
              className="w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm disabled:bg-slate-100"
              data-testid="leave-gun-sayisi"
            />
          </div>

          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-700 block mb-1">Açıklama</label>
            <input
              value={form.aciklama}
              onChange={(e) => setForm({ ...form, aciklama: e.target.value })}
              placeholder="İsteğe bağlı not"
              className="w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm"
              data-testid="leave-aciklama"
            />
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-slate-200">
          <button onClick={onClose} className="px-3 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100 rounded-md">İptal</button>
          <button
            onClick={save}
            disabled={saving}
            data-testid="leave-save-btn"
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold bg-teal-600 hover:bg-teal-700 text-white rounded-md disabled:opacity-50"
          >
            {saving ? "Kaydediliyor..." : (isEdit ? "Güncelle" : "Kaydet")}
          </button>
        </div>
      </div>
    </div>
  );
}

// ===========================================================================
// Monthly Leave Calendar
// ===========================================================================
const AY_ADLARI = [
  "Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran",
  "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık",
];
const GUN_ADLARI_SHORT = ["Pzt", "Sal", "Çar", "Per", "Cum", "Cmt", "Paz"];

function LeaveCalendar({ year, month, onYearChange, onMonthChange, onEditLeave }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hoverEntry, setHoverEntry] = useState(null);
  const [posFilter, setPosFilter] = useState(new Set()); // empty = show all

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/annual-leave/calendar?year=${year}&month=${month}`);
      setData(r.data);
    } catch {
      toast.error("Takvim yüklenemedi");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year, month]);

  const prev = () => {
    if (month === 1) { onMonthChange(12); onYearChange(year - 1); }
    else onMonthChange(month - 1);
  };
  const next = () => {
    if (month === 12) { onMonthChange(1); onYearChange(year + 1); }
    else onMonthChange(month + 1);
  };

  // Distinct positions appearing in this month's leaves
  const monthPositions = useMemo(() => {
    if (!data) return [];
    const seen = new Map();
    for (const d of data.days) {
      for (const e of d.entries) {
        if (e.pozisyon) seen.set(e.pozisyon, (seen.get(e.pozisyon) || 0) + 1);
      }
    }
    return Array.from(seen.entries()).map(([poz, count]) => ({ poz, count }))
      .sort((a, b) => b.count - a.count);
  }, [data]);

  const togglePos = (poz) => {
    setPosFilter((prev) => {
      const next = new Set(prev);
      if (next.has(poz)) next.delete(poz); else next.add(poz);
      return next;
    });
  };
  const clearPos = () => setPosFilter(new Set());

  // Filter entries by selected positions (empty filter = show all)
  const filterEntries = (entries) => {
    if (posFilter.size === 0) return entries;
    return entries.filter((e) => posFilter.has(e.pozisyon));
  };

  if (loading && !data) return <div className="text-sm italic text-slate-500 py-8 text-center">Takvim yükleniyor...</div>;
  if (!data) return null;

  // Compute padding cells before day 1 (align week start to Pzt).
  const firstWeekday = new Date(year, month - 1, 1).getDay(); // 0=Sun ... 6=Sat
  const paddingBefore = firstWeekday === 0 ? 6 : firstWeekday - 1;
  const todayISO = new Date().toISOString().slice(0, 10);

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-3 space-y-3" data-testid="leave-calendar">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="inline-flex items-center gap-1 border border-slate-300 rounded-md overflow-hidden">
          <button onClick={prev} className="w-8 h-8 grid place-items-center text-slate-600 hover:bg-slate-100" data-testid="cal-prev"><ChevronLeft size={14}/></button>
          <div className="px-3 py-1.5 font-bold text-slate-900 min-w-[140px] text-center" data-testid="cal-label">
            {AY_ADLARI[month - 1]} {year}
          </div>
          <button onClick={next} className="w-8 h-8 grid place-items-center text-slate-600 hover:bg-slate-100" data-testid="cal-next"><ChevronRight size={14}/></button>
        </div>
        <div className="text-xs text-slate-500">
          Bu ayda <strong className="text-teal-700">{data.leave_count}</strong> izin kaydı · Hafta sonu ve resmi tatiller gri · <span className="inline-flex items-center gap-1"><span className="inline-block w-2 h-2 bg-indigo-500 rounded-sm" />Arefe yarım gün</span>
        </div>
      </div>

      {monthPositions.length > 0 && (
        <div className="flex items-center gap-1.5 flex-wrap pb-1 border-b border-slate-200" data-testid="cal-pos-filter">
          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Pozisyon Filtresi:</span>
          <button
            type="button"
            onClick={clearPos}
            className={`inline-flex items-center text-[11px] font-semibold px-2 py-0.5 rounded-full border transition ${posFilter.size === 0 ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"}`}
            data-testid="cal-pos-all"
          >
            Hepsi ({monthPositions.reduce((a, b) => a + b.count, 0)})
          </button>
          {monthPositions.map(({ poz, count }) => {
            const pal = getPozisyonColor(poz);
            const active = posFilter.has(poz);
            return (
              <button
                key={poz}
                type="button"
                onClick={() => togglePos(poz)}
                data-testid={`cal-pos-${poz}`}
                className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full border transition ${active ? "ring-2 ring-offset-1 ring-slate-900" : ""}`}
                style={{ backgroundColor: pal.chipBg, borderColor: pal.chipBorder, color: pal.chipText }}
                title={active ? "Filtre'den çıkar" : "Sadece bunu göster"}
              >
                <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: pal.dotBg }} />
                {poz} <span className="opacity-70">({count})</span>
              </button>
            );
          })}
          {posFilter.size > 0 && (
            <button type="button" onClick={clearPos} className="text-[10px] text-slate-500 underline hover:text-slate-900" data-testid="cal-pos-clear">
              temizle
            </button>
          )}
        </div>
      )}

      <div className="grid grid-cols-7 gap-1 text-[10px] font-bold uppercase tracking-wider text-slate-500 border-b border-slate-200 pb-1">
        {GUN_ADLARI_SHORT.map((g, i) => (
          <div key={g} className={`text-center ${i >= 5 ? "text-rose-600" : ""}`}>{g}</div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1">
        {Array.from({ length: paddingBefore }).map((_, i) => (
          <div key={`pad-${i}`} className="bg-slate-50 border border-slate-100 rounded-md min-h-[90px]" />
        ))}
        {data.days.map((day) => {
          const isToday = day.date === todayISO;
          const dayNum = parseInt(day.date.slice(-2), 10);
          const bg = day.holiday
            ? "bg-rose-50 border-rose-200"
            : day.is_arefe
              ? "bg-indigo-50 border-indigo-300"
              : day.is_weekend
                ? "bg-slate-50 border-slate-200"
                : "bg-white border-slate-200";
          // Diagonal stripe overlay for arefe
          const arefeStripe = day.is_arefe
            ? {
                backgroundImage:
                  "repeating-linear-gradient(45deg, rgba(99,102,241,0.08) 0 6px, transparent 6px 12px)",
              }
            : undefined;
          const dayEntries = filterEntries(day.entries);
          return (
            <div
              key={day.date}
              className={`${bg} border rounded-md min-h-[90px] p-1.5 relative flex flex-col ${isToday ? "ring-2 ring-teal-500" : ""}`}
              style={arefeStripe}
              data-testid={`cal-day-${day.date}`}
            >
              <div className="flex items-center justify-between mb-1">
                <span className={`text-xs font-bold ${day.is_weekend ? "text-rose-600" : day.is_arefe ? "text-indigo-700" : "text-slate-700"}`}>{dayNum}</span>
                {day.holiday && (
                  <span className="text-[8px] font-bold uppercase bg-rose-200 text-rose-900 px-1 rounded leading-none py-0.5" title={day.holiday}>
                    Tatil
                  </span>
                )}
                {!day.holiday && day.is_arefe && (
                  <span
                    className="text-[9px] font-black bg-indigo-600 text-white px-1.5 rounded leading-none py-0.5"
                    title={day.arefe_half_day
                      ? "Arefe — iş günü hesabında 0,5 gün sayılır"
                      : "Arefe — yarım gün toggle KAPALI, tam gün sayılıyor"}
                    data-testid={`cal-arefe-${day.date}`}
                  >
                    {day.arefe_half_day ? "½" : "AREFE"}
                  </span>
                )}
              </div>
              {day.holiday && <div className="text-[9px] text-rose-700 font-semibold truncate mb-0.5" title={day.holiday}>{day.holiday}</div>}
              {!day.holiday && day.is_arefe && <div className="text-[9px] text-indigo-700 font-semibold truncate mb-0.5">Arefe{day.arefe_half_day ? " · yarım gün" : ""}</div>}
              <div className="space-y-0.5 flex-1 overflow-hidden">
                {dayEntries.slice(0, 3).map((e) => {
                  const pal = getPozisyonColor(e.pozisyon);
                  return (
                    <button
                      key={e.leave_id}
                      type="button"
                      onClick={() => onEditLeave?.({
                        id: e.leave_id,
                        personnel_id: e.personnel_id,
                        baslangic: e.baslangic,
                        bitis: e.bitis,
                        aciklama: e.aciklama,
                      })}
                      onMouseEnter={() => setHoverEntry(e)}
                      onMouseLeave={() => setHoverEntry(null)}
                      className="w-full text-left text-[10px] font-semibold truncate rounded px-1 py-0.5 border hover:opacity-80 transition"
                      style={{ backgroundColor: pal.chipBg, borderColor: pal.chipBorder, color: pal.chipText }}
                      data-testid={`cal-entry-${day.date}-${e.personnel_id}`}
                      title={`${e.ad_soyad}\n${e.pozisyon}\n${fmtDate(e.baslangic)} → ${fmtDate(e.bitis)}`}
                    >
                      {e.ad_soyad.split(" ")[0]}
                    </button>
                  );
                })}
                {dayEntries.length > 3 && (
                  <div className="text-[9px] text-slate-500 font-semibold">+{dayEntries.length - 3} daha</div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {hoverEntry && (
        <div className="text-[11px] bg-slate-900 text-white rounded-md px-2.5 py-1.5 inline-flex items-center gap-2" data-testid="cal-hover-preview">
          <strong>{hoverEntry.ad_soyad}</strong>
          <span className="opacity-70">{hoverEntry.pozisyon}</span>
          <span className="opacity-70">· {fmtDate(hoverEntry.baslangic)} → {fmtDate(hoverEntry.bitis)}</span>
        </div>
      )}
    </div>
  );
}

function TurBadge({ tur }) {
  const map = {
    yillik:        { label: "YILLIK",   cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
    ucretli_izin:  { label: "ÜCRETLİ",  cls: "bg-indigo-100 text-indigo-800 border-indigo-200" },
    rapor:         { label: "RAPOR",    cls: "bg-rose-100 text-rose-800 border-rose-200" },
    ucretsiz_izin: { label: "ÜCRETSİZ", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  };
  const m = map[tur] || map.yillik;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] border px-1.5 py-0.5 rounded font-bold ${m.cls}`}>
      {m.label}
    </span>
  );
}
